// src/features/landing/PageSettingsForm.tsx
// Editor "Settings" tab — title/slug/description, LandingPageSettings (SEO,
// access, analytics) and LandingPageTheme. Saved via UpdateLandingPage.

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { Input, Textarea, Button, Alert } from '@react/components';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import type { LandingPage } from '@core/types/landing';
import { errMessage } from './helpers';

interface PageSettingsFormProps {
  page: LandingPage;
  onSave: (patch: Partial<LandingPage>) => Promise<void>;
}

export function PageSettingsForm({ page, onSave }: PageSettingsFormProps) {
  const { t } = useI18n();
  const [title, setTitle] = useState(page.title);
  const [description, setDescription] = useState(page.description);
  const [slug, setSlug] = useState(page.slug);
  const [settings, setSettings] = useState(page.settings ?? { is_public: true, show_branding: true });
  const [theme, setTheme] = useState(page.theme ?? {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave({ title, description, slug, settings, theme });
    } catch (e) {
      setError(errMessage(e, t('landing.settings.saveError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      {error && <Alert variant="error">{error}</Alert>}

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">{t('landing.settings.general')}</h3>
        <Input label={t('landing.settings.title')} value={title} onChange={(e) => setTitle(e.target.value)} required />
        <Textarea label={t('landing.settings.description')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        <Input label={t('landing.settings.slug')} value={slug} onChange={(e) => setSlug(e.target.value)} helperText="/l/your-slug" />
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">{t('landing.settings.seo')}</h3>
        <Input label={t('landing.settings.seoTitle')} value={settings.seo_title ?? ''} onChange={(e) => setSettings({ ...settings, seo_title: e.target.value })} />
        <Textarea label={t('landing.settings.seoDescription')} value={settings.seo_description ?? ''} onChange={(e) => setSettings({ ...settings, seo_description: e.target.value })} rows={2} />
        <div className="grid grid-cols-2 gap-3">
          <Input label={t('landing.settings.ogImage')} value={settings.og_image_url ?? ''} onChange={(e) => setSettings({ ...settings, og_image_url: e.target.value })} />
          <Input label={t('landing.settings.favicon')} value={settings.favicon_url ?? ''} onChange={(e) => setSettings({ ...settings, favicon_url: e.target.value })} />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">{t('landing.settings.access')}</h3>
        <ToggleSwitch label={t('landing.settings.isPublic')} description={t('landing.settings.isPublicHelp')} checked={settings.is_public} onChange={(v) => setSettings({ ...settings, is_public: v })} />
        <ToggleSwitch label={t('landing.settings.showBranding')} checked={settings.show_branding} onChange={(v) => setSettings({ ...settings, show_branding: v })} />
        <Input label={t('landing.settings.password')} type="password" value={settings.password ?? ''} onChange={(e) => setSettings({ ...settings, password: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">
          <Input label={t('landing.settings.customDomain')} value={settings.custom_domain ?? ''} onChange={(e) => setSettings({ ...settings, custom_domain: e.target.value })} />
          <Input label={t('landing.settings.redirectUrl')} value={settings.redirect_url ?? ''} onChange={(e) => setSettings({ ...settings, redirect_url: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label={t('landing.settings.analyticsId')} value={settings.analytics_id ?? ''} onChange={(e) => setSettings({ ...settings, analytics_id: e.target.value })} />
          <Input label={t('landing.settings.pixelId')} value={settings.pixel_id ?? ''} onChange={(e) => setSettings({ ...settings, pixel_id: e.target.value })} />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">{t('landing.settings.theme')}</h3>
        <div className="grid grid-cols-2 gap-3">
          <Input label={t('landing.settings.primaryColor')} value={theme.primary_color ?? ''} onChange={(e) => setTheme({ ...theme, primary_color: e.target.value })} placeholder="#3B82F6" />
          <Input label={t('landing.settings.secondaryColor')} value={theme.secondary_color ?? ''} onChange={(e) => setTheme({ ...theme, secondary_color: e.target.value })} placeholder="#111827" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label={t('landing.settings.backgroundColor')} value={theme.background_color ?? ''} onChange={(e) => setTheme({ ...theme, background_color: e.target.value })} placeholder="#ffffff" />
          <Input label={t('landing.settings.textColor')} value={theme.text_color ?? ''} onChange={(e) => setTheme({ ...theme, text_color: e.target.value })} placeholder="#111827" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label={t('landing.settings.fontFamily')} value={theme.font_family ?? ''} onChange={(e) => setTheme({ ...theme, font_family: e.target.value })} placeholder="Inter" />
          <Input label={t('landing.settings.logoUrl')} value={theme.logo_url ?? ''} onChange={(e) => setTheme({ ...theme, logo_url: e.target.value })} />
        </div>
        <Textarea label={t('landing.settings.customCss')} value={theme.custom_css ?? ''} onChange={(e) => setTheme({ ...theme, custom_css: e.target.value })} rows={4} />
      </section>

      <Button onClick={save} disabled={saving}>
        {saving ? t('common.saving') : t('common.save')}
      </Button>
    </div>
  );
}
